import { useState, useMemo, useEffect } from 'react'
import { format, parse } from 'date-fns'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Calendar } from '@/components/ui/calendar'
import { Calendar as CalendarIcon } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { cn } from '@/lib/utils'
import {
  TIME_SLOTS,
  addMinutesToTime,
  formatTimeLabel,
  formatTimezoneLabel,
  instantForZonedWallTime,
} from './scheduling-utils'

const DURATION_OPTIONS = [
  { label: '15m', minutes: 15 },
  { label: '30m', minutes: 30 },
  { label: '60m', minutes: 60 },
]


const TIMEZONES = (() => {
  try {
    return (
      Intl as unknown as { supportedValuesOf: (key: string) => string[] }
    ).supportedValuesOf('timeZone')
  } catch {
    return [
      'UTC',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Sao_Paulo',
      'Europe/London',
      'Europe/Paris',
      'Europe/Berlin',
      'Asia/Dubai',
      'Asia/Kolkata',
      'Asia/Bangkok',
      'Asia/Singapore',
      'Asia/Makassar',
      'Asia/Shanghai',
      'Asia/Tokyo',
      'Australia/Sydney',
      'Pacific/Auckland',
    ]
  }
})()

/** When an interview runs: a local date and start/end wall times in `timezone`. */
export interface InterviewSchedule {
  /** yyyy-MM-dd; '' until a time is booked. */
  date: string
  /** HH:mm; '' until a time is booked. */
  startTime: string
  endTime: string
  /** Minutes. */
  duration: number
  /** IANA zone the wall times are in. */
  timezone: string
  /**
   * Booked with "Immediately": the interview starts when it is created, and
   * date/startTime only hold the slot the picker highlighted.
   */
  immediate?: boolean
}

/**
 * The interview booking dialog: duration, timezone, a calendar day and a time
 * slot (or Immediately). Digital Coworker's Setup Interviews and Knowledge
 * Capture's New Topic both book with it, so the one feature looks the same in
 * both places.
 */
function ScheduleInterviewPicker({
  interview,
  onConfirm,
}: {
  interview: InterviewSchedule
  onConfirm: (schedule: InterviewSchedule) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [duration, setDuration] = useState(interview.duration)
  const [timezone, setTimezone] = useState(interview.timezone)
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    interview.date
      ? parse(interview.date, 'yyyy-MM-dd', new Date())
      : undefined
  )
  const [selectedTime, setSelectedTime] = useState(interview.startTime)
  const [isImmediate, setIsImmediate] = useState(!interview.startTime)

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDuration(interview.duration)
      setTimezone(interview.timezone)
      const hasExisting = !!interview.startTime
      if (hasExisting) {
        setSelectedDate(
          parse(interview.date, 'yyyy-MM-dd', new Date())
        )
        setSelectedTime(interview.startTime)
        setIsImmediate(!!interview.immediate)
      } else {
        const nowInTz = new Date(
          new Date().toLocaleString('en-US', { timeZone: interview.timezone })
        )
        const nowMinutes = nowInTz.getHours() * 60 + nowInTz.getMinutes()
        const nextSlot = Math.max(
          Math.min(Math.ceil(nowMinutes / 15) * 15, 23 * 60 + 45),
          6 * 60
        )
        if (nextSlot + interview.duration >= 24 * 60) {
          // Too late today — advance to tomorrow
          const tomorrow = new Date(nowInTz)
          tomorrow.setDate(tomorrow.getDate() + 1)
          setSelectedDate(tomorrow)
          setSelectedTime('06:00')
        } else {
          setSelectedDate(nowInTz)
          const h = Math.floor(nextSlot / 60)
          const m = nextSlot % 60
          setSelectedTime(
            `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
          )
        }
        setIsImmediate(true)
      }
    }
    setOpen(nextOpen)
  }

  const availableSlots = useMemo(() => {
    let slots = TIME_SLOTS

    // Filter out past time slots if the selected date is today
    if (selectedDate) {
      const nowInTz = new Date(
        new Date().toLocaleString('en-US', { timeZone: timezone })
      )
      const isToday =
        selectedDate.getFullYear() === nowInTz.getFullYear() &&
        selectedDate.getMonth() === nowInTz.getMonth() &&
        selectedDate.getDate() === nowInTz.getDate()
      if (isToday) {
        const nowMinutes = nowInTz.getHours() * 60 + nowInTz.getMinutes()
        const nextSlotMinutes = Math.ceil(nowMinutes / 15) * 15
        slots = slots.filter((slot) => {
          const [h, m] = slot.value.split(':').map(Number)
          return h * 60 + m >= nextSlotMinutes
        })
      }
    }

    // Filter out slots where start + duration would exceed end of day
    const maxStartMinutes = 24 * 60 - duration
    slots = slots.filter((slot) => {
      const [h, m] = slot.value.split(':').map(Number)
      return h * 60 + m < maxStartMinutes
    })

    return slots
  }, [selectedDate, timezone, duration])

  // Clear selectedTime if it's no longer valid after duration/slot changes
  useEffect(() => {
    if (!selectedTime) return
    const stillValid = availableSlots.some((s) => s.value === selectedTime)
    if (!stillValid) {
      setSelectedTime('')
      setIsImmediate(false)
    }
  }, [availableSlots, selectedTime])

  const endTime = selectedTime
    ? addMinutesToTime(selectedTime, duration)
    : ''

  const canConfirm = !!selectedDate && !!selectedTime

  const handleConfirm = () => {
    if (!selectedDate || !selectedTime) return
    onConfirm({
      date: format(selectedDate, 'yyyy-MM-dd'),
      startTime: selectedTime,
      endTime,
      duration,
      timezone,
      immediate: isImmediate,
    })
    setOpen(false)
  }

  const handleImmediately = () => {
    const nowInTz = new Date(
      new Date().toLocaleString('en-US', { timeZone: timezone })
    )
    const nowMinutes = nowInTz.getHours() * 60 + nowInTz.getMinutes()
    const nextSlot = Math.max(
      Math.min(Math.ceil(nowMinutes / 15) * 15, 23 * 60 + 45),
      6 * 60
    )
    if (nextSlot + duration >= 24 * 60) {
      // Too late today — advance to tomorrow
      const tomorrow = new Date(nowInTz)
      tomorrow.setDate(tomorrow.getDate() + 1)
      setSelectedDate(tomorrow)
      setSelectedTime('06:00')
    } else {
      setSelectedDate(nowInTz)
      const h = Math.floor(nextSlot / 60)
      const m = nextSlot % 60
      setSelectedTime(
        `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
      )
    }
    setIsImmediate(true)
  }

  const handleTimeSelect = (timeValue: string) => {
    setSelectedTime(timeValue)
    setIsImmediate(false)
  }

  const confirmationParts = useMemo(() => {
    if (!selectedDate || !selectedTime) return null
    const dateStr = format(selectedDate, 'EEE, MMM d')
    const startStr = formatTimeLabel(selectedTime)
    const endStr = formatTimeLabel(endTime)
    // The booked instant, not the day's local midnight: on a transition day the
    // two are on different offsets (UI-15 review). The summary names the zone
    // as well as the offset, so it and the picker describe the zone alike.
    const offset = formatTimezoneLabel(
      timezone,
      instantForZonedWallTime(timezone, selectedDate, selectedTime),
    )
    return { dateStr, startStr, endStr, offset }
  }, [selectedDate, selectedTime, endTime, timezone])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 shrink-0">
          <CalendarIcon className="h-4 w-4" />
          {t('digital_coworker:setup_interviews.schedule_button')}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-[800px] p-0 gap-0 rounded-2xl shadow-2xl overflow-hidden"
        hideDefaultClose
        overlayClassName="bg-black/30"
      >
        <DialogTitle className="sr-only">
          {t('digital_coworker:setup_interviews.schedule_button')}
        </DialogTitle>

        <div className="flex h-[420px]">
          {/* Left panel – Duration & Timezone */}
          <div className="p-5 space-y-8 border-r dark:border-dark-border w-[210px] shrink-0">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-dark-text mb-3">
                {t('digital_coworker:setup_interviews.interview_duration')}
              </p>
              <div className="flex items-center gap-0.5 rounded-lg bg-gray-100 dark:bg-dark-accent p-0.5">
                {DURATION_OPTIONS.map((opt) => (
                  <button
                    key={opt.minutes}
                    type="button"
                    onClick={() => setDuration(opt.minutes)}
                    className={cn(
                      'flex-1 py-1 text-sm rounded-md transition-all text-center',
                      duration === opt.minutes
                        ? 'bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text font-semibold shadow-sm'
                        : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-gray-300'
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-dark-text mb-3">
                {t('digital_coworker:setup_interviews.timezone_label')}
              </p>
              <Select value={timezone} onValueChange={setTimezone}>
                {/* The trigger's line clamp broke the label at its space, so
                    "Asia/Kolkata (GMT+5:30)" showed only "Asia/Kolkata" with
                    room left over; the label now fills the width and ends in
                    an ellipsis. */}
                <SelectTrigger className="w-full [&>span]:line-clamp-none [&>span]:min-w-0">
                  <SelectValue>
                    <span className="block truncate">
                      {formatTimezoneLabel(
                        timezone,
                        selectedDate && selectedTime
                          ? instantForZonedWallTime(timezone, selectedDate, selectedTime)
                          : undefined
                      )}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {/* Zone + its offset, so this and the summary line below
                          name the same zone the same way (UI-15). */}
                      {formatTimezoneLabel(
                        tz,
                        selectedDate && selectedTime
                          ? instantForZonedWallTime(tz, selectedDate, selectedTime)
                          : undefined
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Center – Calendar (sized + styled to match Figma 11777-99195) */}
          <div className="border-r dark:border-dark-border flex-1 flex items-start justify-center overflow-hidden">
            <Calendar
              className={cn(
                'p-4',
                // Wider, taller day cells to match the Figma reference.
                '[--rdp-day-height:44px] [--rdp-day-width:44px]',
                '[--rdp-day_button-height:40px] [--rdp-day_button-width:40px]',
                '[--rdp-weekday-padding:0_0_8px_0]',
                '[--rdp-nav_button-height:32px] [--rdp-nav_button-width:32px]',
                // Strike-out past/disabled dates so users can see why they
                // can't pick them (matches the "16/17/18" struck-out look in
                // the Figma).
                '[&_.rdp-day_disabled]:line-through [&_.rdp-day_disabled]:text-muted-foreground',
                // Subtle background for "today" so it reads as a hint, not
                // a primary selection.
                '[&_.rdp-day_today:not(.rdp-day_selected)>button]:bg-muted [&_.rdp-day_today:not(.rdp-day_selected)>button]:font-semibold',
                // Outside-month days appear dimmed.
                '[&_.rdp-day_outside]:text-muted-foreground/60',
              )}
              mode="single"
              selected={selectedDate}
              onSelect={(date: Date | undefined) => {
                setSelectedDate(date)
                setIsImmediate(false)
              }}
              disabled={(date: Date) => {
                const nowInTz = new Date(
                  new Date().toLocaleString('en-US', { timeZone: timezone })
                )
                nowInTz.setHours(0, 0, 0, 0)
                return date < nowInTz
              }}
            />
          </div>

          {/* Right panel – Time slots */}
          <div className="w-[180px] shrink-0 overflow-y-auto px-4 py-4 space-y-2">
            <button
              type="button"
              onClick={handleImmediately}
              className={cn(
                'w-full py-2.5 text-sm font-medium rounded-lg border transition-colors',
                isImmediate
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-white dark:bg-dark-card text-gray-700 dark:text-gray-300 border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-muted'
              )}
            >
              {t('digital_coworker:setup_interviews.immediately')}
            </button>
            {availableSlots.map((slot) => (
              <button
                key={slot.value}
                type="button"
                onClick={() => handleTimeSelect(slot.value)}
                className={cn(
                  'w-full py-2.5 text-sm font-medium rounded-lg border transition-colors',
                  !isImmediate && selectedTime === slot.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-white dark:bg-dark-card text-gray-700 dark:text-gray-300 border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-muted'
                )}
              >
                {slot.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bottom confirmation bar */}
        {canConfirm && confirmationParts && (
          <div className="flex items-center justify-between border-t dark:border-dark-border px-6 py-4 bg-gray-50 dark:bg-dark-muted">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {isImmediate
                ? t('digital_coworker:setup_interviews.scheduled_immediately', { duration })
                : t('digital_coworker:setup_interviews.scheduled_summary', {
                    date: confirmationParts.dateStr,
                    startTime: confirmationParts.startStr,
                    endTime: confirmationParts.endStr,
                    timezone: confirmationParts.offset,
                  })}
            </p>
            <Button
              onClick={handleConfirm}
              className="ml-4 shrink-0"
            >
              {t('digital_coworker:setup_interviews.confirm_booking')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

/**
 * The "Schedule" row an interview form shows: its label, the picker's button
 * and, once a time is booked, a line naming it.
 */
export default function ScheduleInterviewField({
  schedule,
  onChange,
}: {
  schedule: InterviewSchedule
  onChange: (schedule: InterviewSchedule) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="border-t border-b py-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold">
            {t('digital_coworker:setup_interviews.schedule_label')}
          </p>
          <p className="text-xs text-gray-500 dark:text-dark-text-muted">
            {t('digital_coworker:setup_interviews.schedule_description')}
          </p>
        </div>
        <ScheduleInterviewPicker interview={schedule} onConfirm={onChange} />
      </div>
      {schedule.date && schedule.startTime && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
          {schedule.immediate
            ? t('digital_coworker:setup_interviews.scheduled_immediately', {
                duration: schedule.duration,
              })
            : t('digital_coworker:setup_interviews.scheduled_summary', {
                date: format(parse(schedule.date, 'yyyy-MM-dd', new Date()), 'EEE, MMM d'),
                startTime: formatTimeLabel(schedule.startTime),
                endTime: formatTimeLabel(schedule.endTime),
                timezone: formatTimezoneLabel(
                  schedule.timezone,
                  instantForZonedWallTime(
                    schedule.timezone,
                    parse(schedule.date, 'yyyy-MM-dd', new Date()),
                    schedule.startTime,
                  ),
                ),
              })}
        </p>
      )}
    </div>
  )
}
